/**
 * QuotaDashboard 组件
 *
 * 配额仪表板，展示所有供应商的配额使用概况。
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  searchAdminApi,
  type SearchQuotaItem,
} from '../../../services/searchAdminApi';
import { QuotaProgress } from '../shared';

const quotaTypeLabels: Record<string, string> = {
  concurrency: '并发',
  requests_per_minute: 'RPM',
  requests_per_hour: '每小时',
  requests_per_day: '每日',
  requests_per_month: '每月',
  requests_total: '总计',
};

export const QuotaDashboard: React.FC<{
  autoRefresh?: boolean;
  refreshInterval?: number;
}> = ({ autoRefresh = true, refreshInterval = 30000 }) => {
  const [quotaData, setQuotaData] = useState<Record<
    string,
    Record<string, SearchQuotaItem[]>
  > | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchQuota = useCallback(async () => {
    try {
      const result = await searchAdminApi.getQuotaUsage();
      setQuotaData(result.quotas);
    } catch (err) {
      setError(err instanceof Error ? err.message : '获取配额数据失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchQuota();
    if (!autoRefresh) return;
    const interval = setInterval(fetchQuota, refreshInterval);
    return () => clearInterval(interval);
  }, [fetchQuota, autoRefresh, refreshInterval]);

  if (loading) {
    return (
      <div className="text-center py-8 text-gray-500">
        加载配额数据中...
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded text-red-600 text-sm">
        {error}
      </div>
    );
  }

  if (!quotaData || Object.keys(quotaData).length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        暂无配额数据。请先配置供应商和密钥。
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {Object.entries(quotaData).map(([providerName, keys]) => (
        <div
          key={providerName}
          className="bg-white rounded-lg border border-gray-200 p-4"
        >
          <h3 className="text-sm font-medium text-gray-700 mb-4">
            {providerName}
          </h3>
          <div className="space-y-4">
            {Object.entries(keys).map(([keyIndex, quotas]) => (
              <div key={keyIndex}>
                <div className="text-xs text-gray-500 mb-2 font-medium">
                  密钥 #{keyIndex}
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {quotas.map((q) => (
                    <QuotaProgress
                      key={`${q.quota_type}-${q.key_index}`}
                      quota={{
                        used: q.used,
                        total: q.limit,
                      }}
                      label={quotaTypeLabels[q.quota_type] || q.quota_type}
                      size="sm"
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default QuotaDashboard;
